import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import {
  Modal,
  Button,
  Input,
  Label,
  Toggle,
} from '@atrvd/saas-ui'
import { useUpdateFacility, SAGE_CHECK_REFRESH_WARNING } from '@/hooks/useBanking'
import {
  facilityFormSchema,
  normalizeOptionalFacilityField,
  type FacilityFormData,
} from '@/lib/facility-form'
import { formatFacilityMutationError } from '@/lib/facility-errors'
import type { Facility } from '@/types/banking'

/**
 * Props for {@link EditFacilityDialog}.
 *
 * @property open - Controls dialog visibility.
 * @property onOpenChange - Called when the dialog should open/close.
 * @property facility - The facility being edited; `null` while no row is selected.
 */
interface EditFacilityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  facility: Facility | null
}

/**
 * Modal dialog for editing a single facility from the settings page.
 *
 * Supports renaming and toggling active state. Pack membership is managed
 * separately from the Packs settings page (DCP-171), not here.
 *
 * @param props - See {@link EditFacilityDialogProps}.
 */
export function EditFacilityDialog({ open, onOpenChange, facility }: EditFacilityDialogProps) {
  const updateFacility = useUpdateFacility()

  /**
   * Last server-side mutation error, surfaced inline above the form so
   * it stays visible after the transient toast fades. Cleared on next
   * submit and on dialog close. The toast still fires for immediate
   * notification; the inline block is the durable record the operator
   * can re-read while figuring out how to fix the input.
   */
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FacilityFormData>({
    resolver: zodResolver(facilityFormSchema),
  })

  /**
   * Reset the form on a *meaningful* transition only: when the dialog
   * goes closed → open, OR when the target facility id changes while
   * the dialog is open. We track the previous `facility.id` in a ref
   * so a parent re-render that mints a fresh `facility` object (same
   * row, new reference — e.g. React Query refetch resolved while the
   * dialog was open) doesn't wipe out the user's in-progress edits.
   *
   * Cleared on close so the next open is treated as a fresh edit
   * session and re-seeds the form from the current `facility` snapshot.
   * Also clears any lingering server error from a previous open session.
   */
  const prevFacilityIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (open && facility) {
      if (facility.id !== prevFacilityIdRef.current) {
        reset({
          name: facility.name,
          sage_location_id: facility.sage_location_id ?? '',
          code: facility.code ?? '',
          is_active: facility.is_active,
        })
        prevFacilityIdRef.current = facility.id
        setServerError(null)
      }
    } else if (!open) {
      prevFacilityIdRef.current = null
    }
  }, [facility, open, reset])

  /** Close the dialog and clear form state */
  const handleClose = () => {
    reset()
    setServerError(null)
    onOpenChange(false)
  }

  /**
   * Handle form submission — sends only the editable fields to the API.
   * On success shows a toast and closes the dialog; on failure surfaces the
   * server message (or a generic fallback) via an error toast.
   *
   * @param values - Validated form values produced by Zod.
   */
  const onSubmit = async (values: FacilityFormData) => {
    if (!facility) return

    setServerError(null)
    try {
      const result = await updateFacility.mutateAsync({
        id: facility.id,
        data: {
          name: values.name,
          sage_location_id: values.sage_location_id,
          // Code is optional in the DB — persist empty input as null so the
          // table cell renders the fallback dash instead of an empty string.
          code: normalizeOptionalFacilityField(values.code),
          is_active: values.is_active,
        },
      })
      toast.success('Facility updated')
      // The facility itself saved; only the follow-up re-link of its Sage checks
      // failed (DCP-308 review #5). Warn rather than fail so the operator knows
      // Outstanding Checks may lag instead of silently reading a stale cache.
      if (result.checkRefreshError) {
        toast.warning(SAGE_CHECK_REFRESH_WARNING)
      }
      handleClose()
    } catch (error) {
      const errorMessage = formatFacilityMutationError(error, 'Failed to update facility')
      setServerError(errorMessage)
      toast.error(errorMessage)
    }
  }

  const currentIsActive = watch('is_active')

  // DCP-318: a facility PULLED FROM Sage Intacct (source = 'sage_intacct') has a
  // Sage Facility ID owned by Sage — the user must not edit it. Only manually
  // created facilities keep it editable.
  const isSageSourced = facility?.source === 'sage_intacct'

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Edit Facility"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={updateFacility.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-facility-form"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={updateFacility.isPending}
          >
            {updateFacility.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Update
          </Button>
        </div>
      }
    >
      <form
        id="edit-facility-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 px-1"
      >
        {/*
          Legacy facility banner — surfaces the DCP-154 product
          constraint (Sage mapping is now mandatory on edit, see
          facility-form.ts:11-16) BEFORE the user starts typing,
          rather than letting them hit the Zod "Sage Facility ID is
          required" error only on submit. Checks for both `null` and
          whitespace-only string so a malformed legacy migration that
          left empty strings instead of NULL also lights up the warning.
        */}
        {facility && !facility.sage_location_id?.trim() && (
          <div
            id="edit-facility-sage-id-legacy-banner"
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <p className="font-medium">Sage Facility ID required</p>
            <p className="mt-1">
              This facility was created before Sage mapping was required.
              Please provide a Sage Facility ID below to save your changes.
            </p>
          </div>
        )}

        {/*
          Inline server-error block — sibling to the per-field Zod errors
          below. Stays visible until the next submit attempt or dialog
          close, unlike the transient toast.
        */}
        {serverError && (
          <div
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            {serverError}
          </div>
        )}

        {/* Facility Name */}
        <div className="space-y-2">
          <Label htmlFor="edit-facility-name">
            Facility Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-facility-name"
            placeholder="Enter facility name"
            disabled={updateFacility.isPending}
            {...register('name')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Sage Facility ID */}
        <div className="space-y-2">
          <Label htmlFor="edit-facility-sage-id">
            Sage Facility ID <span className="text-destructive">*</span>
          </Label>
          {/*
            `aria-describedby` links the input to the legacy-facility
            banner above (when shown) so screen readers announce the
            "Sage Facility ID required" message as soon as focus lands
            on the field — sighted users see the amber banner visually,
            SR users hear it through the input description. The id is
            referenced conditionally: when the banner isn't rendered,
            `aria-describedby` is undefined so the input has no dangling
            reference.
          */}
          <Input
            id="edit-facility-sage-id"
            placeholder="Enter Sage location ID"
            disabled={updateFacility.isPending}
            // DCP-318: read-only (NOT `disabled`) for Sage-sourced facilities so
            // the value stays in the submit payload — react-hook-form drops a
            // `disabled` field to undefined, which would fail the required check.
            readOnly={isSageSourced}
            className={
              isSageSourced ? 'cursor-not-allowed bg-muted text-muted-foreground' : undefined
            }
            aria-describedby={
              isSageSourced
                ? 'edit-facility-sage-id-managed-note'
                : facility && !facility.sage_location_id?.trim()
                  ? 'edit-facility-sage-id-legacy-banner'
                  : undefined
            }
            {...register('sage_location_id')}
          />
          {isSageSourced && (
            <p
              id="edit-facility-sage-id-managed-note"
              className="text-sm text-muted-foreground"
            >
              This ID comes from Sage Intacct and can’t be edited.
            </p>
          )}
          {errors.sage_location_id && (
            <p className="text-sm text-destructive">{errors.sage_location_id.message}</p>
          )}
        </div>

        {/* Facility Code */}
        <div className="space-y-2">
          <Label htmlFor="edit-facility-code">Facility Code</Label>
          <Input
            id="edit-facility-code"
            placeholder="Enter facility code (optional)"
            disabled={updateFacility.isPending}
            {...register('code')}
          />
        </div>

        {/* Status Toggle */}
        <div className="space-y-2">
          <Label>Status</Label>
          <Toggle
            checked={currentIsActive ?? false}
            onChange={(checked: boolean) => setValue('is_active', checked)}
            disabled={updateFacility.isPending}
            label={currentIsActive ? 'Active' : 'Inactive'}
            labelPosition="right"
          />
        </div>
      </form>
    </Modal>
  )
}
